import { FiChevronLeft } from 'react-icons/fi'
import { faPlain, toman } from 'utilities/helper/format'

/**
 * «از کدام صندوق؟» — the first question, and the one the old flow never asked
 * because there was no cross-fund way in at all.
 *
 * Each row states the capital, so the answer to «کدام صندوق پول کافی دارد؟» is
 * on the screen where the choice is made rather than one step later. Funds
 * with an open request are shown but not selectable: a member may hold only
 * one open request per fund, and finding that out on the submit button after
 * four answered questions would be the worst possible moment.
 *
 * When that is true of every fund, disabled rows alone would read as a broken
 * screen — so the step says why once, in a sentence, and offers the one thing
 * left to do: go and look at the request already in flight.
 */
export default function StepFund({
    funds,
    selectedId,
    onSelect,
    onTrack,
}: {
    funds: Array<IDepositFund>
    selectedId: number | null
    onSelect: (fund: IDepositFund) => void
    onTrack?: (fund: IDepositFund) => void
}) {
    const blockedFunds = funds.filter((fund) => fund.open_request)
    const allBlocked = funds.length > 0 && blockedFunds.length === funds.length

    return (
        <div className=" flex flex-col gap-2">
            {funds.map((fund) => {
                const blocked = Boolean(fund.open_request)
                return (
                    <button
                        key={fund.moneypool_id}
                        type="button"
                        disabled={blocked}
                        onClick={() => onSelect(fund)}
                        className={` flex w-full items-center justify-between gap-2 rounded-2xl border bg-white p-3 text-right ${
                            selectedId === fund.moneypool_id
                                ? ' border-brand'
                                : ' border-stroke'
                        } ${blocked ? ' opacity-60' : ''}`}
                    >
                        <span className=" min-w-0">
                            <span className=" block truncate text-sm font-bold text-heading">
                                {fund.moneypool_name}
                            </span>
                            <span className=" mt-[2px] block text-[11px] text-text-secondary">
                                سرمایهٔ شما: {toman(fund.capital)}
                            </span>
                            {fund.open_request && (
                                <span className=" mt-[2px] block text-[11px] text-warning-on">
                                    یک درخواست باز به مبلغ{' '}
                                    {toman(fund.open_request.amount)} دارید
                                </span>
                            )}
                            {fund.hibah > 0 && (
                                <span className=" mt-[2px] block text-[11px] text-text-muted">
                                    هبهٔ شما {toman(fund.hibah)} است و قابل
                                    برداشت نیست
                                </span>
                            )}
                        </span>
                        {!blocked && (
                            <FiChevronLeft className=" shrink-0 text-text-muted" />
                        )}
                    </button>
                )
            })}

            {allBlocked && (
                <div className=" rounded-2xl border border-warning-1 bg-warning-soft p-3 text-right text-[11px] leading-6 text-warning-on">
                    <p>
                        در هر صندوق هم‌زمان یک درخواست برداشت باز می‌توانید
                        داشته باشید. تا بسته شدن{' '}
                        {funds.length === 1 ? 'آن' : 'آن‌ها'}، درخواست تازه‌ای
                        ثبت نمی‌شود.
                    </p>
                    {onTrack && (
                        <button
                            type="button"
                            onClick={() => onTrack(blockedFunds[0])}
                            className=" mt-2 font-bold underline"
                        >
                            پیگیری درخواست باز
                        </button>
                    )}
                </div>
            )}

            {funds.length === 0 && (
                <p className=" rounded-2xl border border-stroke bg-white p-4 text-center text-xs leading-6 text-text-secondary">
                    هنوز در هیچ صندوقی سرمایه‌ای ندارید.
                </p>
            )}

            {funds.length > 0 && (
                <p className=" mt-1 text-[11px] leading-6 text-text-muted">
                    در {faPlain.format(funds.length)} صندوق سرمایه دارید.
                </p>
            )}
        </div>
    )
}
